import { useEffect, useMemo } from 'react';

import { useLazyQuery } from '@apollo/client';
import { PricingTerm, ProductType } from '@prisma/client';
import { useField } from 'formik';
import { ServiceType } from 'types/graphql';

import { OptionTypes } from 'src/components/FormikComponents/DropDown';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { GET_PRICING_OFFERS } from 'src/graphql/queries/pricing.query';

import { filterNullSalesValues, formatProductPricing } from './utils';

type SalesCommisions = {
    sales: number;
    commission: number | null;
};

type ProductPricing = {
    oneOff: SalesCommisions[];
    monthly: SalesCommisions[];
};

type OfferProduct = {
    id: number;
    name: string;
    type?: ProductType;
    imageUrl?: string;
    price?: number;
    description?: string;
    options?: ProductPricing;
};

export type OfferProducts = {
    primaryProducts: OfferProduct[];
    secondaryProducts: OfferProduct[];
};

export const useServiceTypeOffers = () => {
    const [{ value: serviceType }] = useField<ServiceType>('service.serviceType');
    const [{ value: contractTermValue }] = useField({
        name: 'service.contractTerm',
    });
    const { merchantApplication } = useAddMerchantContext();

    const [getOffers, { data }] = useLazyQuery(GET_PRICING_OFFERS);

    const contractTerm = useMemo(() => {
        if (contractTermValue) {
            const contractTerm = Object.values(PricingTerm)
                .filter((el) => el !== PricingTerm.CUSTOM)
                .find((el) => el.includes(contractTermValue));

            return contractTerm ? contractTerm : PricingTerm.CUSTOM;
        } else {
            return undefined;
        }
    }, [contractTermValue]);

    useEffect(() => {
        const partnerId = merchantApplication?.partnerId;
        if (serviceType === 'TERMINAL' && partnerId && contractTerm) {
            // return all terminal offers
            getOffers({
                variables: {
                    filter: { type: 'TERMINAL', partnerId, term: contractTerm },
                },
            });
        } else if (serviceType === 'ONLINE' && partnerId && contractTerm) {
            // return all online offers
            getOffers({
                variables: {
                    filter: { type: 'ONLINE', partnerId, term: contractTerm },
                },
            });
        }
    }, [serviceType, contractTerm, merchantApplication?.partnerId, getOffers]);

    const pricingOffers = useMemo(() => {
        const offers = data?.pricingOffers ?? [];
        if (Array.isArray(offers) && offers.length > 0) {
            return offers.map((offer) => {
                return { ...filterNullSalesValues(offer) };
            });
        } else {
            return [];
        }
    }, [data]);

    const offerProducts: OfferProducts[] = useMemo(() => {
        return pricingOffers.map((offer) => {
            return {
                primaryProducts: offer?.primaryProducts?.map((product) =>
                    formatProductPricing(product, offer)
                ),
                secondaryProducts: offer?.secondaryProducts?.map((product) =>
                    formatProductPricing(product, offer)
                ),
            };
        });
    }, [pricingOffers]);

    const { pricingOptions } = useMemo(() => {
        let options: OptionTypes[] = [];
        if (['TERMINAL', 'ONLINE'].includes(serviceType)) {
            const offers = data?.pricingOffers ?? [];

            options = offers.map((offer) => ({
                value: offer?.id,
                label: offer?.name,
            }));
        }

        return { pricingOptions: options };
    }, [data, serviceType]);

    return { serviceType, pricingOptions, offerProducts, pricingOffers };
};
